import { apiFetch } from "@/lib/api";

export type StockingOrderProgress =
  | "needs_batch"
  | "needs_shipment"
  | "needs_transfer_plan"
  | "transfer_planned"
  | "transfer_allocated"
  | "complete"
  | "cancelled";

export interface StockingOrderShipmentRow {
  shipment_id: number;
  batch_id: number;
  batch_item_id: number;
  date_shipped: string | null;
  transfer_request_id: number | null;
  transfer_request_status: string | null;
}

export interface StockingOrderListRow {
  build_order_id: number;
  build_order_number: string | null;
  sku: string;
  location_id: number;
  location_name: string | null;
  location_abbreviation: string | null;
  ordered_quantity: number;
  received_quantity: number;
  assigned_quantity: number;
  remaining_quantity: number;
  hub_status: string | null;
  cm_purchase_order_id: number | null;
  cm_purchase_order_number: string | null;
  batch_id: number | null;
  batch_item_id: number | null;
  transfer_destination_location_id: number | null;
  transfer_destination_name: string | null;
  progress: StockingOrderProgress;
  shipments: StockingOrderShipmentRow[];
}

export const STOCKING_ORDERS_QUERY_KEY = ["manufacturing", "stocking-orders"] as const;

export async function fetchStockingOrders(input: {
  locationId?: number | null;
  progress?: StockingOrderProgress | "all";
  search?: string;
}): Promise<{ rows: StockingOrderListRow[] }> {
  const params = new URLSearchParams();
  if (input.locationId != null) {
    params.set("location_id", String(input.locationId));
  }
  if (input.progress != null && input.progress !== "all") {
    params.set("progress", input.progress);
  }
  if (input.search != null && input.search.trim() !== "") {
    params.set("search", input.search.trim());
  }
  const suffix = params.toString();
  return apiFetch<{ rows: StockingOrderListRow[] }>(
    suffix === "" ? "/api/manufacturing/stocking-orders" : `/api/manufacturing/stocking-orders?${suffix}`,
  );
}

export async function createStockingOrderBatch(input: {
  buildOrderId: number;
  quantity: number;
  destinationLocationId: number;
  requestKey: string;
  mfrShipByDate?: string | null;
}): Promise<{ batch_id: number; batch_item_id: number; created: boolean }> {
  return apiFetch<{ batch_id: number; batch_item_id: number; created: boolean }>(
    `/api/manufacturing/stocking-orders/${String(input.buildOrderId)}/batch`,
    {
      method: "POST",
      body: JSON.stringify({
        quantity: input.quantity,
        destination_location_id: input.destinationLocationId,
        request_key: input.requestKey,
        mfr_ship_by_date: input.mfrShipByDate ?? null,
      }),
    },
  );
}

export async function createStockingShipmentTransferRequests(input: {
  shipmentId: number;
  targetDate: string;
  notes?: string | null;
}): Promise<{
  transfer_requests: Array<{ transfer_request_id: number; batch_item_id: number; created: boolean }>;
}> {
  return apiFetch(
    `/api/manufacturing/stocking-orders/shipments/${String(input.shipmentId)}/transfer-requests`,
    {
      method: "POST",
      body: JSON.stringify({
        target_date: input.targetDate,
        notes: input.notes ?? null,
      }),
    },
  );
}
